import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isMultiProduct } from "utils/productUtils";
import { isAccountRole, isBusinessRole } from "utils/roleUtils";
import { Box, IconButton, Typography, Menu, MenuItem, Divider, Button, Stack, AppBar, Toolbar } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import ProductSelector from "./Components/ProductSelector";
import { faChargingStation, faTruck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import UserContext from "Context/UserProvider";
import { useAuthService } from "service/AuthService/AuthService";
import {
    CHARGE,
    SHARE,
    SHARE_AND_CHARGE
} from "utils/categoryUtils";
import SelectedFleetModal from "./Components/SelectedFleetModal";
import NotificationsIcon from '@mui/icons-material/Notifications';

// import { PromptProps } from "react-router-dom";

function Nav(props: any) {
    const { logout } = useAuthService();
    const navigate = useNavigate();
    const [modalIsOpen, setIsOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { userInfo, selectedProduct } = useContext(UserContext);
    const open = Boolean(anchorEl);
    const sidebar = props.sidebar;

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleProfileClick = () => {
        navigate("/common/profile");
        handleClose();
    };

    const handleLogout = () => {
        logout();
        handleClose();
    };

    function openModal() {
        setIsOpen(true);
    }

    // console.log("userInfo " + JSON.stringify(userInfo));

    const [currentOpen, setCurrentOpen] = useState("");


    const closeDropDown = useCallback((e: any) => {
        if (e.target.id !== "nav-drop-item") {
            if (e.target.id === "manage-account-btn") {
                navigate("/common/profile");
            } else if (e.target.id === "logout-btn") {
                logout();
            }
            setCurrentOpen("");
        }
    }, []);
    useEffect(() => {
        if (currentOpen !== "") {
            document.addEventListener("click", closeDropDown, true);
        } else {
            document.removeEventListener("click", closeDropDown, true);
        }

        return () => {
            document.removeEventListener("click", closeDropDown, true);
        };
    }, [currentOpen]);

    return (
        <>
            {modalIsOpen && (
                <SelectedFleetModal
                    modalIsOpen={[modalIsOpen, setIsOpen]}
                    userInfo={props.userInfo}
                />
            )}
            <AppBar
                position="fixed"
                color="primary"
                elevation={0}
                sx={{
                    bgcolor: '#2A3137',
                    '& .MuiToolbar-root': {
                        paddingLeft: 0,
                        paddingRight: 0
                    }
                }}
            >
                <Toolbar sx={{ 
                    justifyContent: 'space-between', 
                    minHeight: 64,
                    px: { xs: 0, sm: 0, md: 0 }
                }}>
                    {/* Left section */}
                    <Stack 
                        direction="row" 
                        alignItems="center" 
                        spacing={2}
                    >
                        <IconButton
                            color="inherit"
                            onClick={props.controlSideBar}
                            sx={{ display: { xs: 'none', md: 'none' }, mr: 2 }}
                        >
                            <MenuIcon />
                        </IconButton>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <img
                                src="/img/evata-leaf-logo-64x64.png"
                                alt="Evata Logo"
                                style={{
                                    width: 48,
                                    height: 48,
                                    objectFit: 'contain'
                                }}
                            />
                            <Typography
                                variant="h4"
                                noWrap
                                component="div"
                                sx={{
                                    display: { xs: 'none', sm: 'block' },
                                    color: '#BEF213',
                                    fontFamily: '"Qualy"',
                                    fontWeight: 700,
                                    fontSize: '2rem',

                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    props.setSidebar(true);
                                    navigate(`/${selectedProduct?.toLowerCase()}`);
                                }}
                            >
                                Evata
                            </Typography>
                        </Box>

                        {/* Fleet info */}
                        {(isAccountRole(userInfo.userType) || isBusinessRole(userInfo.userType)) && (
                            <Button
                                onClick={() => {
                                    openModal();
                                }}
                                disableElevation
                                sx={{
                                    display: { xs: 'none', md: 'flex' },
                                    textTransform: 'none',
                                    minWidth: 88,
                                    px: 2,
                                    py: 1,
                                    color: '#BEF213',
                                    borderRadius: 1,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        bgcolor: 'rgba(190, 242, 19, 0.08)',
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                {userInfo.businessName} - {userInfo.currentFleetName}
                            </Button>
                        )}

                        {/* Business Category Icons */}
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ display: { xs: 'none', md: 'flex' } }}
                        >
                            {userInfo.businessCategory === SHARE_AND_CHARGE && (
                                <>
                                    <FontAwesomeIcon icon={faTruck} size="lg" color="#BEF213" />
                                    <FontAwesomeIcon icon={faChargingStation} size="lg" color="#BEF213" />
                                </>
                            )}
                            {userInfo.businessCategory === CHARGE && (
                                <FontAwesomeIcon icon={faTruck} size="lg" color="#BEF213" />
                            )}
                            {userInfo.businessCategory === SHARE && (
                                <FontAwesomeIcon icon={faChargingStation} size="lg" color="#BEF213" />
                            )}
                        </Stack>
                    </Stack>

                    {/* Right section */}
                    <Stack direction="row" spacing={1} alignItems="center">
                        {isMultiProduct(userInfo.businessProducts) && (
                            <Stack
                                direction="row"
                                spacing={0.5}
                                sx={{
                                    display: 'flex',
                                    mr: { xs: 1, md: 2 }
                                }}
                            >
                                <ProductSelector
                                    businessProducts={userInfo.businessProducts}
                                    product="PLAN"
                                    title="Plan"
                                />
                                <ProductSelector
                                    businessProducts={userInfo.businessProducts}
                                    product="SHARE"
                                    title="Share"
                                />
                                <ProductSelector
                                    businessProducts={userInfo.businessProducts}
                                    product="AGREE"
                                    title="Agree"
                                />
                                <ProductSelector
                                    businessProducts={userInfo.businessProducts}
                                    product="CHARGE"
                                    title="Charge"
                                />
                            </Stack>
                        )}

                        <Button
                            onClick={handleClick}
                            startIcon={<AccountCircleIcon sx={{ color: '#BEF213' }} />}
                            disableElevation
                            sx={{
                                display: { xs: 'none', md: 'flex' },
                                textTransform: 'none',
                                bgcolor: open ? 'rgba(190, 242, 19, 0.12)' : 'transparent',
                                color: '#BEF213',
                                borderRadius: 1,
                                boxShadow: 'none',
                                px: 2,
                                py: 1,
                                '&:hover': {
                                    bgcolor: 'rgba(190, 242, 19, 0.08)',
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            My Profile
                        </Button>

                        {!props.landing && (
                            <IconButton
                                edge="end"
                                sx={{
                                    display: { xs: 'flex', md: 'none' },
                                    color: '#BEF213'
                                }}
                                onClick={props.controlSideBar}
                            >
                                {!sidebar ? <MenuIcon /> : <CloseIcon />}
                            </IconButton>
                        )}

                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            onClick={handleClose}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            PaperProps={{
                                elevation: 0,
                                sx: {
                                    overflow: 'visible',
                                    mt: 1.5,
                                    border: 1,
                                    borderColor: 'divider',
                                    '& .MuiAvatar-root': {
                                        width: 32,
                                        height: 32,
                                        ml: -0.5,
                                        mr: 1,
                                    },
                                },
                            }}
                        >
                            <MenuItem onClick={handleProfileClick}>
                                <AccountCircleIcon sx={{ mr: 1 }} />
                                My Profile
                            </MenuItem>
                            <Divider />
                            <MenuItem onClick={handleLogout}>
                                <LogoutIcon sx={{ mr: 1 }} />
                                Sign out
                            </MenuItem>
                        </Menu>
                    </Stack>
                </Toolbar>
            </AppBar>
            {/* Add toolbar spacing */}
            <Toolbar />
        </>
    );
}

export function ProfileLink(props: any) {

    const navigate = useNavigate();

    return (
        <li className="item">
            <a
                onClick={() => {
                    navigate("/common/profile");
                }}>
                <span className="material-symbols-outlined icon">account_circle</span>
                <span
                    className="title"
                    id="nav-drop-item">
                    My Profile
                </span>
            </a>
        </li>
    );
}


export function Navbar(props: any) {
    return (
        <header
            className="header"
            id="header">
            {props.children}
        </header>
    );
}

export function NavItem(props: any) {

    const { logout } = useAuthService();

    return (
        <li className="item">
            <a
                onClick={() => {
                    if (props.logout) {
                        logout();
                    } else {
                        props.dropMenu(props.elem);
                    }
                }}>
                <span className="material-symbols-outlined icon">{props.icon}</span>
                <span
                    className="title"
                    id="nav-drop-item">
                    {props.title}
                </span>
            </a>
            {props.notification && props.notificationCount > 0 && <span className="notification-count">{props.notificationCount}</span>}
            {props.open && props.children}
        </li>
    );
}

export function DropdownMenu(props: any) {
    const navigate = useNavigate();

    const { logout } = useAuthService();

    const [activeMenu, setActiveMenu] = useState(props.type);
    const [menuHeight, setMenuHeight] = useState(0) as any;
    const dropdownRef = useRef() as any;

    useEffect(() => {
        setMenuHeight(dropdownRef.current.firstChild.offsetHeight);
    }, []);

    function calcHeight(el: any) {
        const height = el.offsetHeight;
        setMenuHeight(height);
    }

    function DropdownItem(props: any) {
        return (
            <a
                className="menu-item"
            >
                <span className={props.profile ? "icon-profile" : "icon-button"}>{props.leftIcon}</span>
                {props.children}
                <span className="icon-right">{props.rightIcon}</span>
            </a>
        );
    }

    return (
        <div
            className="dropdown"
            ref={dropdownRef}>
            {/* start main menu */}


            <div className="profile">
                <div className="info">
                    <div className="name-and-email">
                        <span className="font-weight-bold">{props.user}</span>
                        <span>{props.user}</span>
                    </div>
                </div>
                <div className="actions">
                    <button
                        onClick={() => ""}
                        type="button"
                        className="button"
                        id="manage-account-btn">
                        Manage Account
                    </button>
                    <button
                        type="button"
                        id="logout-btn"
                        className="button sign-out">
                        Sign out
                    </button>
                </div>
            </div>
        </div>
    );
}
export function NotiContent(props: any) {
    return (
        <div className="noti-content">
            <a className="menu-item">
                <span className={props.profile ? "icon-profile" : "icon-button"}>{props.leftIcon}</span>
                {props.children}
                <span className="icon-right">{props.rightIcon}</span>
            </a>
            <div className="d-flex flex-column">
                <p>You have been upload the file, here is the file number that you uplod the first thing is the</p>
                <p style={{ color: "grey" }}>6 days ago</p>
            </div>
        </div>
    );
}

export default Nav;
